import React, { useEffect, useState, useRef } from "react";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { useNavigate } from "react-router-dom";
import RequestService from "../../services/requestService";
import MessengerService from "../../services/messengerService";
import LocationService from "../../services/locationService";
import ProviderService from "../../services/providerservice";
import {
  GetDataFullName,
  GetDataEmail,
} from "../../components/utils/data-storage";
import { showSuccess, showError } from "../../components/utils/swal-helpers";
import SearchableSelect from "../../components/common/searchable-select";

const WF = {
  CREATE: 1,
  USER_CANCEL: 2,
  RETURN: 3,
  ADMIN_CANCEL: 4,
  ASSIGN: 5,
  DOING: 6,
  COMPLETE: 7,
  INCOMPLETE: 8,
  REASSIGN: 9,
  CLOSE_OK: 10,
  CLOSE_COND: 11,
  USER_RESPONDED: 19,
};

const WF_TAB_MAP = {
  new: [WF.CREATE],
  inprogress: [WF.ASSIGN, WF.DOING, WF.REASSIGN, WF.RETURN],
  review: [WF.COMPLETE, WF.INCOMPLETE],
  done: [WF.CLOSE_OK, WF.CLOSE_COND, WF.USER_CANCEL, WF.ADMIN_CANCEL],
};

// ── sub-tabs breakdown ภายในแต่ละ main tab (แสดงเฉพาะ inprogress/review/done) ──
const SUB_TAB_MAP = {
  inprogress: [
    { key: "all", label: "All", wf: WF_TAB_MAP.inprogress },
    { key: "assign", label: "In Progress", wf: [WF.ASSIGN, WF.REASSIGN] },
    { key: "doing", label: "In Delivery", wf: [WF.DOING] },
    { key: "return", label: "Return", wf: [WF.RETURN] },
  ],
  review: [
    { key: "all", label: "All", wf: WF_TAB_MAP.review },
    { key: "complete", label: "Delivered", wf: [WF.COMPLETE] },
    { key: "incomplete", label: "Failed", wf: [WF.INCOMPLETE] },
  ],
  done: [
    { key: "all", label: "All", wf: WF_TAB_MAP.done },
    { key: "close_ok", label: "Completed", wf: [WF.CLOSE_OK] },
    { key: "close_cond", label: "Done with Condition", wf: [WF.CLOSE_COND] },
    { key: "cancel", label: "Cancel", wf: [WF.USER_CANCEL, WF.ADMIN_CANCEL] },
  ],
};

const TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "inprogress", label: "In Progress" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];

const SORT_OPTIONS = [
  { key: "date_desc", label: "วันที่ล่าสุด" },
  { key: "date_asc", label: "วันที่เก่าสุด" },
];

const ROWS_OPTIONS = [10, 20, 30, 40, 50];

// ── sub_service_type สำหรับ sType=3 (ชำระบริการ) ──
// ── key อ้างอิง id จริงจาก m_sub_service_type (4=ไฟฟ้า, 5=ประปา, 6=โทรศัพท์) ──
const SUB_SERVICE_TYPE_LABELS = {
  4: "ค่าไฟ",
  5: "ค่าน้ำ",
  6: "ค่าโทรศัพท์",
};

const formatDate = (value) => {
  if (!value) return "-";
  const [y, m, day] = String(value).slice(0, 10).split("-");
  return `${day}/${m}/${y}`;
};

// ── ชื่อ provider สำรอง เมื่อไม่มี receiver ใน service_type = 3 (ชำระค่าบริการ) ──
// ── key อ้างอิง id จริงจาก m_sub_service_type (4=ไฟฟ้า, 5=ประปา, 6=โทรศัพท์) ──
const TRANSACTION_PROVIDER_NAMES = {
  4: "การไฟฟ้านครหลวง / ภูมิภาค",
  5: "การประปานครหลวง / ภูมิภาค",
  6: "ผู้ให้บริการโทรศัพท์",
};

// ── HELPER: เช็คว่า provider branch เป็น "อื่นๆ" หรือไม่ (ใช้กับ sType 2/3 เท่านั้น) ──
// ── backend เก็บ 0 = "อื่นๆ", ยังเช็ค -1 ควบคู่เพื่อรองรับข้อมูลเก่า (sType 2 เดิม) ──
const isOtherProviderBranch = (detail) => {
  const val = Number(detail?.destination_id_provider_branch);
  return val === 0 || val === -1;
};

const getReceiver = (detail) => {
  if (!detail) return "-";
  if (detail.id_service_type === 3) {
    if (isOtherProviderBranch(detail)) {
      return detail.destination_name || "-";
    }
    return (
      detail.destination_company ||
      TRANSACTION_PROVIDER_NAMES[detail.id_sub_service_type] ||
      "-"
    );
  }
  if (detail.id_service_type === 2) {
    if (isOtherProviderBranch(detail)) {
      return detail.destination_name || "-";
    }
    return detail.destination_company || detail.destination_contact_name || "-";
  }
  return (
    detail.destination_contact_name ||
    detail.destination_name ||
    detail.destination_company ||
    "-"
  );
};

const getReceiverCompany = (detail, bankProviders = {}) => {
  if (!detail) return "-";
  if (detail.id_service_type === 2) {
    return (
      bankProviders[detail.destination_id_provider] ||
      detail.destination_company ||
      "-"
    );
  }
  return detail.destination_company || detail.origin_building_name || "-";
};

const getServiceTypeLabel = (detail, deliveryTypeMap) => {
  if (!detail) return "-";
  if (detail.id_service_type === 1) {
    return deliveryTypeMap[detail.id_sub_service_type] || "-";
  }
  if (detail.id_service_type === 3) {
    return SUB_SERVICE_TYPE_LABELS[detail.id_sub_service_type] || "-";
  }
  if (detail.id_service_type === 2) {
    return "บริการธนาคาร";
  }
  return "-";
};

const getDestination = (detail) => {
  if (!detail) return "-";

  if (detail?.id_service_type === 2 || detail?.id_service_type === 3) {
    if (detail?.destination_provider !== null) {
      return (
        detail?.destination_provider?.name_th ||
        detail?.destination_provider?.name_en ||
        "-"
      );
    }
  } else {
    if (detail?.destination_company !== "") {
      return detail?.destination_company || "-";
    }
  }

  return "-";
};

const getBuilding = (detail) => {
  if (!detail) return "-";

  if (detail?.id_service_type === 2 || detail?.id_service_type === 3) {
    if (detail?.destination_provider_branch !== null) {
      return (
        detail?.destination_provider_branch?.name_th ||
        detail?.destination_provider_branch?.name_en ||
        "="
      );
    }

    if (detail?.destination_name !== "") {
      return detail?.destination_name || "";
    }
  } else {
    if (detail?.destination_building !== null) {
      return (
        detail?.destination_building?.name_th ||
        detail?.destination_building?.name_en ||
        "-"
      );
    }

    if (detail?.destination_name !== "") {
      return detail?.destination_name || "";
    }
  }

  return "-";
};

const isDocumentService = (detail) => Number(detail?.id_service_type) === 1;

const getDestinationFloor = (detail) => {
  if (!detail?.destination_floor) return "-";
  return `ชั้น ${detail.destination_floor}`;
};

const renderDetailItem = (label, value) => (
  <div key={label} className="ar-modal-detail-item">
    <span className="ar-modal-detail-label">{label}</span>
    <span className="ar-modal-detail-value">{value}</span>
  </div>
);

const getPageNumbers = (currentPage, totalPages) => {
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, currentPage + 2);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const getStatusBadge = (row) => {
  const wf = row.id_workflow_status;
  const details = row.request_details || [];
  const total = details.length;
  const done = details.filter((d) => d.is_complete == 1).length;

  if (wf === WF.CREATE)
    return {
      label: "New",
      cls: "ar-badge--new",
      detailCls: "detail-status-badge--inprogress",
    };
  if (wf === WF.DOING && total > 1)
    return {
      label: `In Delivery (${done + 1}/${total})`,
      cls: "ar-badge--delivery",
      detailCls: "detail-status-badge--indelivery",
    };
  if (wf === WF.DOING)
    return {
      label: "In Delivery",
      cls: "ar-badge--delivery",
      detailCls: "detail-status-badge--indelivery",
    };
  if ([WF.ASSIGN, WF.REASSIGN].includes(wf))
    return {
      label: "In Progress",
      cls: "ar-badge--inprogress",
      detailCls: "detail-status-badge--inprogress",
    };
  if (wf === WF.COMPLETE && total > 1)
    return {
      label: `Delivered (${total}/${total})`,
      cls: "ar-badge--delivered",
      detailCls: "detail-status-badge--delivered",
    };
  if (wf === WF.COMPLETE)
    return {
      label: "Delivered",
      cls: "ar-badge--delivered",
      detailCls: "detail-status-badge--delivered",
    };
  if (wf === WF.INCOMPLETE)
    return {
      label: "Failed",
      cls: "ar-badge--failed",
      detailCls: "detail-status-badge--failed",
    };
  if (wf === WF.RETURN)
    return {
      label: "Return",
      cls: "ar-badge--return",
      detailCls: "detail-status-badge--return",
    };
  // if (wf === WF.USER_RESPONDED) return { label: 'New', cls: 'ar-badge--new', detailCls: 'detail-status-badge--inprogress' };
  if (wf === WF.CLOSE_OK)
    return {
      label: "Completed",
      cls: "ar-badge--completed-icon",
      detailCls: "detail-status-badge--completed",
      type: "icon",
    };
  if (wf === WF.CLOSE_COND)
    return {
      label: "Done with Condition",
      cls: "ar-badge--condition-icon",
      detailCls: "detail-status-badge--completed",
      type: "icon",
    };
  if ([WF.USER_CANCEL, WF.ADMIN_CANCEL].includes(wf))
    return {
      label: "Cancel",
      cls: "ar-badge--cancel",
      detailCls: "detail-status-badge--cancel",
    };
  return { label: "-", cls: "", detailCls: "" };
};

const DETAIL_CLS_ICON = {
  "detail-status-badge--inprogress": "ri-feedback-fill",
  "detail-status-badge--indelivery": "ri-feedback-fill",
  "detail-status-badge--delivered": "mdi mdi-check-circle",
  "detail-status-badge--failed": "ri-feedback-fill",
  "detail-status-badge--return": "ri-feedback-fill",
  "detail-status-badge--completed": "ri-checkbox-circle-fill",
  "detail-status-badge--cancel": "ri-feedback-fill",
};

const isMessengerReadyNow = (m) => {
  if (m.is_outsource !== 1) return true;
  const now = new Date();
  const start = m.login_start_date ? new Date(m.login_start_date) : null;
  const end = m.login_end_date ? new Date(m.login_end_date) : null;
  if (start && now < start) return false; // ยังไม่ถึงวันเริ่มงาน
  if (end && now > end) return false; // เลยวันหมดอายุแล้ว
  return true;
};

// ─────────────────────────────────────────────────────────
// Quick Action Modal
// ─────────────────────────────────────────────────────────

const QuickActionModal = ({
  type,
  messengers,
  bankProviders,
  onClose,
  onConfirm,
}) => {
  const [refNo, setRefNo] = useState("");
  const [messengerId, setMessengerId] = useState("");
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [action, setAction] = useState("reassign");
  // ── districts lookup สำหรับแสดงชื่อเขตจาก id ──
  const [districts, setDistricts] = useState([]);
  const isAssign = type === "assign";

  const DELIVERY_TYPE_MAP = {
    1: "รับเท่านั้น",
    2: "ส่งเท่านั้น",
    3: "ส่งและรับกลับ",
  };
  const DOCUMENT_TYPE_MAP = {
    1: "ปกติ (Normal)",
    2: "Confidential (ลับเฉพาะ)",
  };
  const DELIVERY_SESSION_MAP = {
    1: "Morning/รอบเช้า",
    2: "Afternoon/รอบบ่าย",
    3: "Urgent ด่วน",
  };

  // ── โหลด districts ทั้งหมดครั้งเดียวไว้ lookup ชื่อจาก id ──
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const resp = await LocationService.SearchDistrict({ status: 1 });
        if (resp.data.is_success && resp.data.result) {
          setDistricts(resp.data.result);
        }
      } catch (err) {
        console.error("loadDistricts error:", err);
      }
    };
    loadDistricts();
  }, []);

  const fetchRequest = async () => {
    if (!refNo.trim()) return;
    setLoading(true);
    setNotFound(false);
    setRequestData(null);
    try {
      const resp = await RequestService.SearchRequest({
        ref_no: refNo.trim(),
        status: 1,
      });
      if (resp.data.is_success && resp.data.result?.length) {
        setRequestData(resp.data.result[0]);
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!requestData) return;
    if (isAssign && !messengerId) return;

    const resolvedAction =
      requestData.id_workflow_status === WF.COMPLETE ? "complete" : action;
    if (!isAssign && resolvedAction === "reassign" && !messengerId) return;

    let finalAction = resolvedAction;
    if (isAssign) {
      if (
        requestData.id_workflow_status === WF.RETURN ||
        requestData.id_messenger > 0
      ) {
        finalAction = "reassign_job";
      } else {
        finalAction = "assign";
      }
    }

    onConfirm({
      refNo: refNo.trim(),
      messengerId,
      requestId: requestData.id,
      action: finalAction,
      wfStatus: requestData.id_workflow_status,
    });
  };

  const detail = requestData?.request_details?.[0];
  const statusBadge = requestData ? getStatusBadge(requestData) : null;
  const messengerInfo = requestData?.messenger;

  // ── COMPLETE_TIME_MAP ต้องอ่านจาก detail (request_detail level) ไม่ใช่ requestData (request level) ──
  const COMPLETE_TIME_MAP = {
    1: "เสร็จวันนี้เท่านั้น",
    2: `ระบุเวลา ภายในเวลา ${
      detail?.complete_time_specific
        ? new Date(detail.complete_time_specific).toTimeString().slice(0, 5)
        : ""
    }`,
  };

  // ── lookup ชื่อเขตจาก destination_id_district ──
  const districtName =
    districts.find(
      (d) => String(d.id) === String(detail?.destination_id_district),
    )?.name_th || "-";

  return (
    <div className="ar-modal-overlay" onClick={onClose}>
      <div className="ar-modal" onClick={(e) => e.stopPropagation()}>
        {!requestData && (
          <div className="ar-modal-header-wrap">
            <div
              className={`ar-modal-header ${isAssign ? "ar-modal-header--assign" : "ar-modal-header--complete"}`}
            >
              <i
                className={`ar-modal-header-icon ${isAssign ? "ri-donut-chart-fill" : "ri-checkbox-circle-fill"}`}
              />
              <span>{isAssign ? "Assign Messenger" : "Complete"}</span>
            </div>
          </div>
        )}

        <div className="ar-modal-body">
          {!requestData && (
            <>
              <div className="ar-modal-scan">
                <i className="ri-qr-code-line" />
                <span>Scan ด้วย QR code</span>
                <span className="ar-modal-scan-sub">หรือ</span>
              </div>
              <div className="ar-modal-field-border">
                <div className="ar-modal-field">
                  <label>Ref No.</label>
                  <input
                    type="text"
                    placeholder="ใส่เลขคำขอ"
                    value={refNo}
                    onChange={(e) => {
                      setRefNo(e.target.value);
                      setNotFound(false);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && fetchRequest()}
                  />
                </div>
              </div>
              {loading && (
                <p
                  style={{
                    textAlign: "center",
                    color: "#94a3b8",
                    marginTop: 8,
                  }}
                >
                  กำลังค้นหา...
                </p>
              )}
              {notFound && (
                <p
                  style={{
                    textAlign: "center",
                    color: "#ef4444",
                    marginTop: 8,
                  }}
                >
                  ไม่พบ Ref No. {refNo}
                </p>
              )}
            </>
          )}

          {requestData && (
            <>
              <div className="ar-modal-ref-row">
                <div className="ar-modal-ref-title">
                  Ref No.{requestData.ref_no}
                </div>
                {statusBadge && (
                  <span
                    className={`detail-status-badge ${statusBadge.detailCls}`}
                  >
                    <i className={DETAIL_CLS_ICON[statusBadge.detailCls]} />
                    {statusBadge.label}
                  </span>
                )}
              </div>

              {isAssign && (
                <div className="ar-modal-field">
                  <label>เลือก Messenger</label>
                  <SearchableSelect
                    id="quick-assign-messenger"
                    name="messenger"
                    value={messengerId}
                    onChange={(e) => setMessengerId(e.target.value)}
                    options={messengers.map((m) => ({
                      value: String(m.id),
                      label: `${m.name} ${m.surname}`,
                    }))}
                    placeholder="Messenger name"
                  />
                </div>
              )}

              {!isAssign && (
                <>
                  <div className="ar-modal-field-border mb-16">
                    {messengerInfo && (
                      <div className="ar-modal-field">
                        <label>Messenger</label>
                        <div className="ar-modal-detail-value">
                          {messengerInfo.name} {messengerInfo.surname}
                        </div>
                      </div>
                    )}
                    {requestData.admin_remark && (
                      <div className="ar-modal-remark-box">
                        {requestData.admin_remark}
                      </div>
                    )}
                  </div>

                  {requestData.id_workflow_status !== WF.COMPLETE && (
                    <>
                      <div className="ar-modal-action-group">
                        {[
                          {
                            value: "reassign",
                            label: "Re-Assign",
                            color: "#F59E0B",
                          },
                          {
                            value: "condition",
                            label: "Done with Condition",
                            color: "#22C55E",
                          },
                          {
                            value: "cancel",
                            label: "Cancel Job",
                            color: "#EF4444",
                          },
                        ].map((opt) => (
                          <label
                            key={opt.value}
                            className={`ar-modal-action-option ${action === opt.value ? "ar-modal-action-option--active" : ""}`}
                            style={{ "--option-color": opt.color }}
                          >
                            <input
                              type="radio"
                              name="complete-action"
                              value={opt.value}
                              checked={action === opt.value}
                              onChange={() => {
                                setAction(opt.value);
                                setMessengerId("");
                              }}
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>

                      {action === "reassign" && (
                        <div className="ar-modal-field">
                          <label>เลือก Messenger</label>
                          <SearchableSelect
                            id="quick-reassign-messenger"
                            name="messenger"
                            value={messengerId}
                            onChange={(e) => setMessengerId(e.target.value)}
                            options={messengers.map((m) => ({
                              value: String(m.id),
                              label: `${m.name} ${m.surname}`,
                            }))}
                            placeholder="Messenger name"
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              <div className="ar-modal-field-border">
                <div className="ar-modal-detail-grid">
                  {[
                    {
                      label: "Service Date",
                      value: formatDate(requestData.service_date),
                    },
                    {
                      label: "Receiver",
                      //   value: getReceiver(detail),
                      value: detail?.destination_contact_name || "-",
                    },
                    {
                      label: "Service Type",
                      value: getServiceTypeLabel(detail, DELIVERY_TYPE_MAP),
                    },
                    {
                      label: "Contact Number",
                      value: detail?.destination_phone_number || "-",
                    },
                    {
                      label: "Request Type",
                      value:
                        DOCUMENT_TYPE_MAP[requestData.document_type] || "-",
                    },
                    {
                      label: "Destination",
                      //   value: getReceiverCompany(detail, bankProviders),
                      value: getDestination(detail),
                    },
                    {
                      label: "Delivery Session",
                      value:
                        DELIVERY_SESSION_MAP[requestData.delivery_section] ||
                        "-",
                    },
                    {
                      label: "Department",
                      value: detail?.destination_department || "-",
                    },
                    {
                      label: "Completion Time",
                      value:
                        COMPLETE_TIME_MAP[detail?.complete_time_type] || "-",
                    },
                    // {
                    //   label: "Building",
                    //   value: detail?.origin_building_name || "-",
                    //   sub: isDocumentService(detail)
                    //     ? { label: "Floor", value: getDestinationFloor(detail) }
                    //     : null,
                    // },
                    {
                      label: "Building",
                      value: getBuilding(detail),
                      sub: isDocumentService(detail)
                        ? { label: "Floor", value: getDestinationFloor(detail) }
                        : null,
                    },
                    { label: "Remark", value: detail?.note || "-" },
                    { label: "District", value: districtName },
                  ].map(({ label, value, sub }) =>
                    sub ? (
                      <div key={label} className="ar-modal-detail-split">
                        {renderDetailItem(label, value)}
                        {renderDetailItem(sub.label, sub.value)}
                      </div>
                    ) : (
                      renderDetailItem(label, value)
                    ),
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="ar-modal-footer">
          <button
            className="ar-modal-btn ar-modal-btn--cancel--popup"
            onClick={onClose}
          >
            Cancel
          </button>
          {requestData && (
            <button
              className={`ar-modal-btn ${
                isAssign
                  ? "ar-modal-btn--assign"
                  : requestData.id_workflow_status === WF.COMPLETE
                    ? "ar-modal-btn--complete-action"
                    : "ar-modal-btn--assign"
              }`}
              onClick={handleConfirm}
              disabled={isAssign && !messengerId}
            >
              {isAssign
                ? requestData.id_messenger > 0 ||
                  requestData.id_workflow_status === WF.RETURN
                  ? "Reassign"
                  : "Assign"
                : requestData.id_workflow_status === WF.COMPLETE
                  ? "Complete"
                  : requestData.id_workflow_status === WF.RETURN
                    ? "Reassign"
                    : "Submit"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────

const AdminRequests = () => {
  const navigate = useNavigate();

  const [dataReq, setDataReq] = useState([]);
  const [total, setTotal] = useState(0);
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    new: 0,
    inprogress: 0,
    review: 0,
    done: 0,
  });
  const [messengers, setMessengers] = useState([]);
  const [quickAction, setQuickAction] = useState(null);
  const [bankProviders, setBankProviders] = useState({});

  const [activeTab, setActiveTab] = useState("all");
  const [activeSubTab, setActiveSubTab] = useState("all");
  const [subTabCounts, setSubTabCounts] = useState({});
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [showRowsDropdown, setShowRowsDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const rowsDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  const userName = GetDataFullName ? GetDataFullName() : "Admin";

  useEffect(() => {
    loadDataRequests(1, rowsPerPage, "all");
    loadTabCounts();
    loadMessengers();
    loadBankProviders();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (
        rowsDropdownRef.current &&
        !rowsDropdownRef.current.contains(e.target)
      )
        setShowRowsDropdown(false);
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(e.target)
      )
        setShowSortDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const loadBankProviders = async () => {
    try {
      const res = await ProviderService.SearchProvider({
        id_service_type: 2,
        status: 1,
      });
      const map = Object.fromEntries(
        (res.data.result || []).map((p) => [p.id, p.name_th]),
      );
      setBankProviders(map);
    } catch (err) {
      console.error("loadBankProviders error:", err);
    }
  };

  const loadDataRequests = async (
    page = 1,
    rows = rowsPerPage,
    tab = activeTab,
    q = search,
    subTab = activeSubTab,
  ) => {
    try {
      const offset = (page - 1) * rows;
      const params = { status: 1, limit: rows, offset };
      if (tab !== "all") {
        const matched = SUB_TAB_MAP[tab]?.find((s) => s.key === subTab);
        params.id_workflow_status = matched ? matched.wf : WF_TAB_MAP[tab];
      }
      if (q) {
        params.search = q;
      }
      const resp = await RequestService.SearchRequest(params);
      if (!resp.data.is_success || !resp.data.result) return;
      setDataReq(resp.data.result);
      setTotal(resp.data.total || 0);
    } catch (err) {
      console.error("Failed to load requests", err);
    }
  };

  const loadSubTabCounts = async (tab) => {
    const subTabs = SUB_TAB_MAP[tab];
    if (!subTabs) return;
    try {
      const counts = {};
      await Promise.all(
        subTabs.map(async (st) => {
          const resp = await RequestService.SearchRequest({
            status: 1,
            limit: 1,
            offset: 0,
            id_workflow_status: st.wf,
          });
          counts[st.key] = resp.data.total || 0;
        }),
      );
      setSubTabCounts(counts);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubTabClick = (subTab) => {
    setActiveSubTab(subTab.key);
    setCurrentPage(1);
    loadDataRequests(1, rowsPerPage, activeTab, search, subTab.key);
  };

  const loadTabCounts = async () => {
    try {
      const counts = { all: 0, new: 0, inprogress: 0, review: 0, done: 0 };
      const allResp = await RequestService.SearchRequest({
        status: 1,
        limit: 1,
        offset: 0,
      });
      counts.all = allResp.data.total || 0;
      await Promise.all(
        Object.entries(WF_TAB_MAP).map(async ([tab, wfList]) => {
          const resp = await RequestService.SearchRequest({
            status: 1,
            limit: 1,
            offset: 0,
            id_workflow_status: wfList,
          });
          counts[tab] = resp.data.total || 0;
        }),
      );
      setTabCounts(counts);
    } catch (err) {
      console.error(err);
    }
  };

  const loadMessengers = async () => {
    try {
      // ── available_only=true: filter เฉพาะ messenger ที่พร้อมใช้งาน (ไม่ได้ทำงานอยู่) ──
      const resp = await MessengerService.SearchMessenger({
        status: 1,
        available_only: true,
      });
      if (!resp.data.is_success || !resp.data.result) return;
      setMessengers(resp.data.result.filter(isMessengerReadyNow));
    } catch (err) {
      console.error("Failed to load messengers", err);
    }
  };

  const handleQuickActionConfirm = async ({
    refNo,
    messengerId,
    requestId,
    action,
    wfStatus,
  }) => {
    setQuickAction(null);
    try {
      if (action === "assign") {
        await RequestService.AssignMessengerRequest(requestId, {
          id_messenger: parseInt(messengerId),
          update_by: GetDataEmail(),
        });
        await showSuccess({
          title: "Assign สำเร็จ",
          detail: `มอบหมายงาน ${refNo} เรียบร้อยแล้ว`,
        });
      } else if (action === "reassign_job") {
        if (wfStatus === WF.RETURN) {
          await RequestService.ChangeWorkflowStatus(requestId, {
            id_workflow_status: WF.ASSIGN,
            update_by: GetDataEmail(),
          });
          await RequestService.ReassignMessengerRequest(requestId, {
            id_messenger: parseInt(messengerId),
            update_by: GetDataEmail(),
          });
        } else {
          await RequestService.ReassignMessengerRequest(requestId, {
            id_messenger: parseInt(messengerId),
            update_by: GetDataEmail(),
          });
        }
        await showSuccess({
          title: "Reassign สำเร็จ",
          detail: `มอบหมายงาน ${refNo} เรียบร้อยแล้ว`,
        });
      } else {
        const actionMap = {
          reassign: () =>
            RequestService.ReassignMessengerRequest(requestId, {
              id_messenger: parseInt(messengerId),
              update_by: GetDataEmail(),
            }),
          condition: () =>
            RequestService.ChangeWorkflowStatus(requestId, {
              id_workflow_status: WF.CLOSE_COND,
              update_by: GetDataEmail(),
            }),
          cancel: () =>
            RequestService.ChangeWorkflowStatus(requestId, {
              id_workflow_status: WF.ADMIN_CANCEL,
              update_by: GetDataEmail(),
            }),
          complete: () =>
            RequestService.ChangeWorkflowStatus(requestId, {
              id_workflow_status: WF.CLOSE_OK,
              update_by: GetDataEmail(),
            }),
        };
        await actionMap[action]?.();
        await showSuccess({
          title: "สำเร็จ",
          detail: `ดำเนินการ ${refNo} เรียบร้อยแล้ว`,
        });
      }
      loadDataRequests(currentPage, rowsPerPage, activeTab);
      loadTabCounts();
    } catch (err) {
      console.error(err);
      showError();
    }
  };

  // ── debounce search เพื่อไม่ยิง API ถี่เกินไปตอนพิมพ์ ──
  const searchTimeoutRef = useRef(null);
  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q);
    setCurrentPage(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadDataRequests(1, rowsPerPage, activeTab, q);
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const onClickRow = (row) => {
    navigate("/admin/request-detail/" + btoa(row.id + "::AR"), {
      replace: true,
    });
  };

  const sorted = [...dataReq].sort((a, b) => {
    if (sortKey === "date_asc")
      return new Date(a.service_date) - new Date(b.service_date);
    if (sortKey === "date_desc")
      return new Date(b.service_date) - new Date(a.service_date);
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const pageNumbers = getPageNumbers(safePage, totalPages);

  const goPage = (p) => {
    if (p >= 1 && p <= totalPages) {
      setCurrentPage(p);
      loadDataRequests(p, rowsPerPage, activeTab);
    }
  };

  const sortLabel =
    SORT_OPTIONS.find((o) => o.key === sortKey)?.label || "Sort By";
  const countLabel =
    total === 0
      ? "0 รายการ"
      : `${(safePage - 1) * rowsPerPage + 1}–${Math.min(safePage * rowsPerPage, total)} จาก ${total} รายการ`;

  return (
    <React.Fragment>
      <div className="admin-requests-page">
        <div className="ar-header">
          <h2>Welcome back, {userName}</h2>
          <p>ภาพรวมคำขอและการดำเนินการด่วน</p>
        </div>

        <div className="ar-quick-actions">
          <p className="ar-quick-actions__label">Quick Actions</p>
          <div className="ar-quick-actions__cards">
            <div
              className="ar-qa-card ar-qa-card--assign"
              onClick={() => setQuickAction("assign")}
            >
              <div className="ar-qa-card__icon-assign">
                <i className="ri-donut-chart-fill" />
              </div>
              <div className="ar-qa-card__body">
                <p className="ar-qa-card__title">Assign Job</p>
                <p className="ar-qa-card__sub">มอบหมายงานให้ Messenger</p>
              </div>
            </div>
            <div
              className="ar-qa-card ar-qa-card--complete"
              onClick={() => setQuickAction("complete")}
            >
              <div className="ar-qa-card__icon-complete">
                <i className="ri-checkbox-circle-fill" />
              </div>
              <div className="ar-qa-card__body">
                <p className="ar-qa-card__title">Complete Job</p>
                <p className="ar-qa-card__sub">ปิดงานจาก Messenger</p>
              </div>
            </div>
          </div>
        </div>

        <div className="ar-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`ar-tab ${activeTab === tab.key ? "ar-tab--active" : ""}`}
              onClick={() => {
                if (searchTimeoutRef.current)
                  clearTimeout(searchTimeoutRef.current);
                setActiveTab(tab.key);
                setActiveSubTab("all");
                setSearch("");
                setSortKey("");
                setCurrentPage(1);
                loadDataRequests(1, rowsPerPage, tab.key, "", "all");
                if (SUB_TAB_MAP[tab.key]) loadSubTabCounts(tab.key);
              }}
            >
              {tab.label}
              <span
                className={`ar-tab-count ${
                  ["new", "review"].includes(tab.key) && activeTab !== tab.key
                    ? "ar-tab-count--alert"
                    : ""
                }`}
              >
                {tabCounts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        <div className="ar-card">
          {SUB_TAB_MAP[activeTab] && (
            <div className="sp-chip">
              <div className="sp-tabs__list">
                {SUB_TAB_MAP[activeTab].map((st) => (
                  <button
                    key={st.key}
                    className={`sp-tab ${activeSubTab === st.key ? "sp-tab--active" : ""}`}
                    onClick={() => handleSubTabClick(st)}
                  >
                    {st.label} {subTabCounts[st.key] ?? 0}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="ar-toolbar">
            <div className="ar-toolbar__left">
              <div className="ar-search">
                <i className="ri-search-line" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className="ar-toolbar__right">
              <span className="ar-pagination-info">{countLabel}</span>

              <div className="ar-rows-dropdown" ref={rowsDropdownRef}>
                <button
                  className="ar-btn-icon"
                  onClick={() => setShowRowsDropdown((v) => !v)}
                >
                  <i className="ri-more-2-fill" />
                </button>
                {showRowsDropdown && (
                  <div className="ar-dropdown-menu">
                    {ROWS_OPTIONS.map((opt) => (
                      <div
                        key={opt}
                        className={`ar-dropdown-item ${rowsPerPage === opt ? "ar-dropdown-item--active" : ""}`}
                        onClick={() => {
                          setRowsPerPage(opt);
                          setCurrentPage(1);
                          loadDataRequests(1, opt, activeTab);
                          setShowRowsDropdown(false);
                        }}
                      >
                        {opt} รายการ
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="ar-sort-dropdown" ref={sortDropdownRef}>
                <button
                  className="ar-sort-btn"
                  onClick={() => setShowSortDropdown((v) => !v)}
                >
                  {sortLabel} <i className="ri-arrow-down-s-line" />
                </button>
                {showSortDropdown && (
                  <div className="ar-dropdown-menu">
                    {SORT_OPTIONS.map((o) => (
                      <div
                        key={o.key}
                        className={`ar-dropdown-item ${sortKey === o.key ? "ar-dropdown-item--active" : ""}`}
                        onClick={() => {
                          setSortKey(o.key);
                          setShowSortDropdown(false);
                        }}
                      >
                        {o.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Ref no.</th>
                  <th>Service date</th>
                  <th>Requester</th>
                  <th>Contact no.</th>
                  <th>Receiver</th>
                  <th>Receiver's company</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="ar-table-empty">
                      ยังไม่มีรายการคำขอ
                    </td>
                  </tr>
                ) : (
                  sorted.map((row, idx) => {
                    const badge = getStatusBadge(row);
                    const detail = row.request_details?.[0];
                    return (
                      <tr key={row.id || idx} onClick={() => onClickRow(row)}>
                        <td>{row.ref_no}</td>
                        <td className="ar-col-muted">
                          {formatDate(row.service_date)}
                        </td>
                        <td>{row.sender_name}</td>
                        <td className="ar-col-muted">
                          {detail?.destination_phone_number || "-"}
                        </td>
                        <td>{getReceiver(detail)}</td>
                        <td>{getReceiverCompany(detail, bankProviders)}</td>
                        <td>
                          {badge.type === "icon" ? (
                            <span className={`ar-status-icon ${badge.cls}`}>
                              <i className="ri-checkbox-circle-fill" />
                              {badge.label}
                            </span>
                          ) : (
                            <span className={`ar-badge ${badge.cls}`}>
                              {badge.label}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="table-pagination">
            <Pagination>
              <PaginationItem disabled={safePage === 1}>
                <PaginationLink first onClick={() => goPage(1)} />
              </PaginationItem>
              <PaginationItem disabled={safePage === 1}>
                <PaginationLink previous onClick={() => goPage(safePage - 1)} />
              </PaginationItem>
              {pageNumbers.map((p) => (
                <PaginationItem key={p} active={p === safePage}>
                  <PaginationLink onClick={() => goPage(p)}>{p}</PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem disabled={safePage === totalPages}>
                <PaginationLink next onClick={() => goPage(safePage + 1)} />
              </PaginationItem>
              <PaginationItem disabled={safePage === totalPages}>
                <PaginationLink last onClick={() => goPage(totalPages)} />
              </PaginationItem>
            </Pagination>
          </div>
        </div>
      </div>

      {quickAction && (
        <QuickActionModal
          type={quickAction}
          messengers={messengers}
          bankProviders={bankProviders}
          onClose={() => setQuickAction(null)}
          onConfirm={handleQuickActionConfirm}
        />
      )}
    </React.Fragment>
  );
};

export default AdminRequests;
